import { css } from "styled-components";
import { CarouselSlide, CarouselTrigger, CarouselViewport } from "../../../../components/Carousel";
import { Image } from "../../../../components/Image";
import { CTA } from "../../../../components/CTA";
import { BackdropBackground, BackdropForeground } from "../../../../components/Backdrop";
import { HeaderBar } from "../../components/HeaderBar";
import { MastheadCarousel } from "../../components/MastheadCarousel";
import { Availability } from "../../components/Availability";
import { hideOnTouchDevices } from "../../../../skins/shared/mixins/mediaQueries";
import { textUppercaseMixin } from "../../../../skins/shared/mixins/typography";
import { CardTitle } from "../../../../components/Card";
const preMobileHeaderBreak = "@media (max-width: 700px)";
export const riotProductSkin = (chroma, paletteTokens) => css `
  & {
    background-color: ${paletteTokens.bladebg1};

    /*  45vw is an approximation of the carousel height,
        which accounts for the responsive card sizes on mobile. */
    --carousel-height: min(240px, 45vw);

    @media (any-hover: none) {
      /* on touch devices remove the height of the carousel buttons which are hidden */
      --carousel-height: calc(min(240px, 45vw) - 20px);
    }

    --bladeHeader-height: 60px;

    max-height: 1100px;
    min-height: 800px;
    height: calc(100vh - ${chroma.dimensions.riotbarHeight});
    margin: 0 auto;
    padding: 0;
    transition: max-height 0.5s ease-in-out;
    ${chroma.mediaQueries.tabletDown} {
      max-height: 1000px;
      min-height: 700px;
    }
    ${chroma.mediaQueries.mobileDown} {
      min-height: 600px;
    }

    .layout {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: block;
      grid-template-columns: 100%;
      grid-template-rows: var(--bladeHeader-height) 1fr 120px 355px;
      grid-column-gap: 0;
      grid-row-gap: 0;

      ${chroma.mediaQueries.tabletDown} {
        grid-template-rows: var(--bladeHeader-height) 1fr 120px 305px;
      }
      ${chroma.mediaQueries.mobileDown} {
        grid-template-rows: 1fr 0 0 min(240px, 45vw);
      }
    }
  }

  ${HeaderBar} {
    margin: 0;
    height: fit-content;
    position: relative;
    overflow: visible;
    display: flex;
    justify-content: space-between;
    align-content: center;
    padding: ${chroma.spacers.variable.m};
    flex-direction: row;

    ${chroma.mediaQueries.tabletDown} {
      align-items: center;
      padding: calc(${chroma.spacers.variable.m} + 10px) ${chroma.spacers.variable.m};
    }

    ${chroma.mediaQueries.mobileDown} {
      height: 100%;
      flex-direction: column;
      background-color: transparent;
      padding: ${chroma.spacers.variable.l};
    }

    .header-heading {
      display: flex;
      flex-direction: column;
      max-width: 35%;
      height: fit-content;
      align-items: flex-start;
      ${chroma.mediaQueries.mobileDown} {
        margin: 0;
        align-items: center;
        height: var(--bladeHeader-height);
        width: 100%;
        max-width: 100%;
        min-width: 60px;
        position: relative;
        top: 0;
        left: 0;
        padding-right: 0;
      }

      min-width: 390px;

      ${chroma.mediaQueries.tabletDown} {
        min-width: 300px;
      }

      ${preMobileHeaderBreak} {
        min-width: 100%;
        width: 100%;
        position: relative;
        height: auto;
        top: 0;
        left: 0;
        padding-right: 0;
      }

      .logo {
        a:focus,
        a:focus-visible,
        a:active {
          img {
            outline: 2px solid ${paletteTokens.accents2};
            outline-offset: 4px;
          }
        }
      }

      .tagline {
        letter-spacing: 0.02em;
        ${preMobileHeaderBreak} {
          text-align: center;
          margin: 0 auto;
        }
      }
    }

    .logo {
      position: relative;
      display: block;
      width: 100%;
      max-width: 450px;
      ${preMobileHeaderBreak} {
        max-width: 350px;
        margin: auto;
      }
      ${Image} {
        height: auto;
        object-fit: contain;
      }
    }

    .tagline {
      position: relative;
      display: block;
      width: 100%;
      color: ${paletteTokens.bladetext1};
      ${chroma.typography.display7};
      ${textUppercaseMixin}
      padding-top: 1em;

      pre {
        white-space: pre-wrap;
        word-break: keep-all; /* this stops the word breaking */
      }

      ${chroma.mediaQueries.tabletDown} {
        max-width: 295px;
      }

      ${chroma.mediaQueries.mobileDown} {
        max-width: 327px;
        margin: auto;
        text-align: center;
      }
    }

    .links {
      width: 100%;
      display: flex;
      align-self: flex-start;
      justify-self: flex-end;
      justify-content: flex-end;
      align-items: center;

      ${chroma.mediaQueries.mobileDown} {
        justify-content: center;
      }

      ${CTA} {
        width: 225px;
        text-align: center;
        vertical-align: bottom;
        white-space: nowrap;

        ${chroma.typography.cta};

        ${textUppercaseMixin}

        ${chroma.mediaQueries.mobileDown} {
          width: fit-content;
        }
      }

      a.tertiary {
        margin-right: 2vw;
        ${chroma.mediaQueries.desktopUp} {
          margin-right: 0;
        }

        ${chroma.mediaQueries.mobileDown} {
          display: none;
        }
      }

      ${CTA}.variant-primary {
        width: fit-content;
        font-weight: 300;

        &:focus,
        :focus-visible {
          outline: 2px solid ${paletteTokens.keyline};
          outline-offset: -14px;
        }
      }

      ${preMobileHeaderBreak} {
        justify-content: center;
      }
    }

    ${preMobileHeaderBreak} {
      height: calc(100% - var(--carousel-height));
      flex-direction: column;
      background-color: transparent;
      padding: ${chroma.spacers.variable.l};
    }
  }

  ${BackdropBackground} {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    > img {
      height: 100%;
      width: 100%;
      object-fit: cover;
      object-position: 50%;
    }
  }
  ${BackdropForeground} {
    display: block;
    padding-top: clamp(0px, 10vh, 160px);
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    ${chroma.mediaQueries.tabletDown} {
      padding-top: clamp(0px, 20vh, 160px);
      padding-bottom: calc(var(--carousel-height) - 20px);
    }

    > img {
      height: 100%;
      width: auto;
      margin: 0 auto;
      object-fit: cover;
      object-position: 50% 0%;
    }
  }

  ${MastheadCarousel} {
    position: absolute;
    bottom: 0;
    padding: 0;
    margin: 0;
    width: 100%;

    &[data-state-all-slides-visible="true"] {
      .trigger-previous,
      .trigger-next {
        display: none;
      }
    }

    .carousel-triggers {
      width: 100%;
      display: flex;
      bottom: 100%; /* aligns bottom of buttons to the top edge of carousel viewport */
      justify-content: flex-end;
      gap: 10px;
      position: relative;
      padding: ${chroma.spacers.variable.m} ${chroma.spacers.variable.l} 0 ${chroma.spacers.variable.m};
      height: fit-content;
      ${chroma.mediaQueries.mobileDown} {
        display: none;
      }

      ${CarouselTrigger} {
        ${hideOnTouchDevices}
      }
    }

    ${CarouselViewport} {
      position: relative;
      padding: ${chroma.spacers.variable.m} ${chroma.spacers.variable.l};

      &:focus,
      :focus-visible {
        outline: none;
      }
    }

    ${CarouselSlide} {
      cursor: pointer;
      min-width: min(330px, 75%);
      align-self: flex-end;
      transition: transform 0.3s ease-in-out;
      margin: 0 ${chroma.spacers.variable.s} 0 0;

      &:last-child {
        margin-right: 0;
      }

      ${chroma.mediaQueries.desktopUp} {
        &:hover {
          transform: scale(1.15);
          z-index: 2;
        }

        &[data-focused="true"] {
          outline: 2px solid ${paletteTokens.keyline};
          outline-offset: 4px;
        }
      }
    }
    ${CardTitle} {
      text-shadow: 2px 3px 7px #000;
      letter-spacing: -0.02em;
      line-height: 19px;
    }

    .icon-action-play-video {
      max-height: 55px;
      max-width: 55px;
    }
  }

  ${Availability} {
    ${chroma.mediaQueries.mobileDown} {
      display: none;
    }

    display: flex;
    position: relative;
    align-items: center;
    justify-content: flex-end;
    height: fit-content;
    min-height: 90px;
    min-width: 188px;
    margin: 0 ${chroma.spacers.variable.m};

    .availability-box {
      width: fit-content;
      border: 1px solid ${paletteTokens.accents1};
      color: ${paletteTokens.bladebg2};
      background-color: ${paletteTokens.bladebg2};

      /* TODO: use typography from chroma */
      font-size: 14px;
      font-weight: 500;
      min-width: 300px;
      max-width: calc(100% - ${chroma.spacers.constant.xxl});

      fieldset {
        --icon-padding: ${chroma.spacers.constant.xs};
        --icon-size: ${chroma.spacers.constant.sm};

        border: none;
        margin: ${chroma.spacers.constant.s};

        legend {
          padding: var(--icon-padding);
          ${chroma.typography.textLink};
          line-height: 9px;
          ${textUppercaseMixin}
          color: ${paletteTokens.bladebg2};
          background: #fff;
          width: 100%;
          margin-bottom: ${chroma.spacers.constant.s};
        }

        ul {
          display: flex;
          flex-wrap: wrap;
          ${textUppercaseMixin}
          content: ${chroma.mediaQueries.desktopUp};

          li {
            height: calc(var(--icon-size) + (2 * var(--icon-padding)));
            width: calc(var(--icon-size) + (2 * var(--icon-padding)));
            padding: var(--icon-padding);
          }

          a {
            color: ${paletteTokens.bladetext3};
            display: inline-block;
            height: var(--icon-size);
            width: var(--icon-size);
            line-height: var(--icon-size);
          }
        }
      }
    }
  }

  &:focus,
  :focus-visible {
    outline: 2px solid ${paletteTokens.keyline};
    outline-offset: 4px;
  }
`;
//# sourceMappingURL=index.js.map